import { lazy, Suspense, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContentGroups, TranslationNamespaces } from '~/lib/constants';
import { Tagged } from '~/apps/smartsend/components/Tagged';
import { ContentContext, useAnalytics } from '@parcel2go/npm-p2g-app';
import { RoutingConstants, RoutingConstantsTabs } from '~/apps/smartsend/lib/consts/RoutingConstants';
import { GenerateBaseAnalyticsPayload } from '~/lib/utils/analytics';
import { NavigationContext } from '~/components/nav/nav-context';
import useOnLoad from '../use-on-load';
import { P2GBox } from '@parcel2go/core-ui';
import { GetDefaultSubItem, SubNavButtonGroup, SubNavItemType } from '../groups/sub-nav';
import { SettingsCache } from '~/lib/consts/settings-cache';

const ReportingOverview = lazy(() => import('~/apps/smartsend/components/reporting/ReportingOverview'));
const PrePay = lazy(() => import('~/apps/prepay/prepay'));

export default function Component() {
  const options: SubNavItemType[] = useMemo(() => {
    const output = [];
    if (SettingsCache.settings.Features.includes('PrePay')) {
      output.push({
        translationKey: 'TAB_PREPAY_LABEL',
        label: 'PrePay',
        tab: RoutingConstantsTabs.PrePay
      });
    }
    output.push({
      translationKey: 'TAB_STATEMENTS_AND_INVOICES_LABEL',
      label: 'Invoices',
      tab: RoutingConstantsTabs.StatementsAndInvoices
    });
    return output;
  }, []);

  const { track } = useAnalytics();
  const { dispatch } = useContext(ContentContext);
  const { dispatch: navigationDispatch } = useContext(NavigationContext);
  const { t } = useTranslation(TranslationNamespaces.MYDETAILS);
  const [selectedOption, setSelectedOption] = useState<string>(GetDefaultSubItem(options));

  useEffect(() => {
    if (selectedOption) {
      window.location.hash = selectedOption;

      track({
        type: 'page_view',
        payload: {
          ...GenerateBaseAnalyticsPayload([]),
          page: selectedOption,
          area: RoutingConstants.Billing
        }
      });
    }
  }, [selectedOption, track]);

  useOnLoad(() => {
    const title = t('BILLING_PAGE_TITLE', { defaultValue: 'Billing' });
    document.title = title;
    dispatch({ type: 'SET_CONTENT_GROUP', contentGroup: ContentGroups.BILLING });
    navigationDispatch({
      type: 'INITIALISE_NAVIGATION',
      payload: { title: title, currentPageRoute: RoutingConstants.Billing }
    });
  });

  return (
    <Tagged tag="screen-mydetails">
      <P2GBox display="flex" flexDirection="column" gap={4}>
        <SubNavButtonGroup
          options={options}
          selectedOption={selectedOption}
          setSelectedOption={setSelectedOption}
          translationNameSpace={TranslationNamespaces.MYDETAILS}
        />
        {selectedOption === RoutingConstantsTabs.PrePay && (
          <Suspense>
            <P2GBox>
              <PrePay />
            </P2GBox>
          </Suspense>
        )}
        {selectedOption === RoutingConstantsTabs.StatementsAndInvoices && (
          <Suspense>
            <P2GBox>
              <ReportingOverview />
            </P2GBox>
          </Suspense>
        )}
      </P2GBox>
    </Tagged>
  );
}

Component.displayName = 'My Details';
